import axios from "axios";
import { FormControl, FormLabel } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import { enqueueSnackbar } from "notistack";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import PageMenu from "../../../../components/custom/app/pageMenu/pageMenu";
import PageContentWrapper from "../../../../components/mui/app/pageContentWrapper/pageContentWrapper";
import PageTitle from "../../../../components/mui/app/pageTitle/pageTitle";
import CustomAutocomplete from "../../../../components/mui/form/autocomplete/autocomplete";
import NavigateBackButton from "../../../../components/mui/general/navigateBackButton/navigateBackButton";
import { API } from "../../../../helpers/constants";
import { handleAxiosErrors, logoutUnauthorizedUser } from "../../../../helpers/errorHandling";
import { formControlDefaults } from "../../../../helpers/theme/defaultProps";
import { getRouteUrl } from "../../../../helpers/routing/getRouteUrl";
import useUserAction from "../../../../hooks/user/useUserAction";
import useUserData from "../../../../hooks/user/useUserData";
import { CHECKLISTS } from "../../../../locales/pages/namespaces";
import { ROUTE_PAGE_ISSUES_EDIT } from "../../routes/routes";
import ChecklistsList from "../../components/nonCompletedChecklists/checklistsList";

const IssuesChecklists = (props) => {
	const { t } = useTranslation(CHECKLISTS)
	const { IssueId, Description } = useParams()
	const { token, isLoggedIn } = useUserData()
	const { userLogout } = useUserAction()
	
	const [checklists, setChecklists] = useState([])
	const [selectedChecklists, setSelectedChecklists] = useState([])
	const [loadingSearch, setLoadingSearch] = useState(false)
	const [loadingAssign, setLoadingAssign] = useState(false)
	const refreshGridRef = useRef(null)
	
	const handleSearch = useCallback((evt) => {
		if (!evt?.target) return
		setLoadingSearch(true)
		axios({
			method: 'post',
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
			data: { query: evt.target.value },
			url: `${API}/en/questionnaire/search`
		}).then((response) => {
			setChecklists(response.data ?? [])
			setLoadingSearch(false)
		}).catch((error) => {
			setLoadingSearch(false)
			enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
				variant: 'error',
				autoHideDuration: 3000,
			})
		})
	}, [token, isLoggedIn, userLogout, t])
	
	const handleAssign = useCallback(() => {
		if (!selectedChecklists.length) return
		setLoadingAssign(true)
		axios({
			method: 'post',
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
			data: {
				IssueId: IssueId,
				Surveys: selectedChecklists.map(c => ({ id: c.id, Description: c.Description }))
			},
			url: `${API}/en/questionnaire-rule/assignment`
		}).then(() => {
			setLoadingAssign(false)
			setSelectedChecklists([])
			enqueueSnackbar(t('Assignment created successfully!'), {
				variant: 'success',
				autoHideDuration: 3000,
			})
			refreshGridRef.current?.()
		}).catch((error) => {
			setLoadingAssign(false)
			enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
				variant: 'error',
				autoHideDuration: 3000,
			})
		})
	}, [token, IssueId, selectedChecklists, isLoggedIn, userLogout, t])
	
	return (
		<Box className={'issues_checklist_index'}>
			<PageContentWrapper>
				<PageMenu>
					<NavigateBackButton
						title={t('Return')}
						fallBackRoute={getRouteUrl(ROUTE_PAGE_ISSUES_EDIT, { IssueId: IssueId })}
					/>
				</PageMenu>
				<PageTitle title={t("Checklists of Issue") + ': ' + `${Description}`}/>
				<Grid container spacing={2} alignItems={'flex-end'} sx={{ mb: 2 }}>
					<Grid size={{ xs: 12, sm: 9 }}>
						<FormControl {...formControlDefaults}>
							<FormLabel>{t('Checklists')}</FormLabel>
							<CustomAutocomplete
								identifier="id"
								labelKey="Description"
								value={selectedChecklists}
								onChange={(evt, newValue) => setSelectedChecklists(newValue)}
								options={checklists}
								loading={loadingSearch}
								otherprops={{
									multiple: true,
									disableCloseOnSelect: true,
									getOptionKey: (option) => option?.id
								}}
								onInputChange={(event) => handleSearch(event)}
							/>
						</FormControl>
					</Grid>
					<Grid size={{ xs: 12, sm: 3 }}>
						<Button
							variant="contained"
							color="primary"
							disabled={loadingAssign || !selectedChecklists.length}
							onClick={handleAssign}
							sx={{ width: '100%' }}
						>
							{loadingAssign ? t('Assigning...') : t('Assign')}
						</Button>
					</Grid>
				</Grid>
				<ChecklistsList
					IssueId={IssueId}
					url={`questionnaire-data/index`}
					onRefreshReady={(fn) => {
						refreshGridRef.current = fn
					}}
				/>
			</PageContentWrapper>
		</Box>
	)
}

export default IssuesChecklists
